import { EnvironmentService } from '@abp/ng.core';
import { NgClass } from '@angular/common';
import {
    Component,
    computed,
    contentChildren,
    Directive,
    inject,
    input,
    output,
    SecurityContext,
    TemplateRef,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

// directive
@Directive({
    selector: 'ng-template[storyImage]',
})
export class StoryImageDirective {
    public template = inject(TemplateRef<never>);
    public src = input<string>();
    public alt = input<string>();
}

@Directive({ selector: '[storyImageSize]' })
export class StoryImageSizeDirective {
    size = input<string | null>(null);
    media = input<string | null>(null);
    src = input<null | string>(null);
}

@Component({ selector: 'story-source', template: '' })
export class StoryImageSourceComponent {
    width = input<number | undefined>(undefined);
    height = input<number | undefined>(undefined);
    src = input<string | undefined>(undefined);
    media = input<string | undefined>(undefined);
    allowWebP = input<boolean>(true);
    allowAvif = input<boolean>(true);
}

type StorySource = {
    type:
        | 'image/avif'
        | 'image/webp'
        | 'image/jpeg'
        | 'image/png'
        | 'image/gif'
        | 'image/svg+xml'
        | 'image/tiff'
        | 'image/bmp'
        | 'image/ico'
        | 'image/webp';
    src: string;
    media: string | undefined;
};

// image component
@Component({
    selector: 'story-image',
    imports: [NgClass],
    template: `
        <picture
            class="block h-full w-full object-cover object-center"
            [ngClass]="{
                'bg-[repeating-linear-gradient(-45deg,_#d6d6d630_0_3px,_#ffffff00_3px_6px)]': computedSrc() === '',
            }"
        >
            @for (source of computedSources(); track $index) {
                <source [attr.srcset]="source.src" [attr.media]="source.media ?? ''" [attr.type]="source.type" />
            }

            <img
                [src]="computedSrc()"
                [loading]="loading()"
                decoding="async"
                class="block h-full w-full {{ objectFit() }} {{ objectPosition() }}"
                [attr.fetchPriority]="fetchPriority()"
                [ngClass]="{
                    'opacity-0': computedSrc() === '',
                }"
                (load)="loaded.emit()"
                [attr.alt]="alt()"
                [attr.role]="role()"
            />
        </picture>
        <ng-content></ng-content>
    `,
})
export class StoryImageComponent {
    private sanitizer = inject(DomSanitizer);
    alt = input<string>();
    width = input<number>();
    height = input<number>();
    loading = input<'lazy' | 'eager'>('lazy');
    fetchPriority = input<'low' | 'auto' | 'high'>('auto');
    objectFit = input<string>('object-cover');
    objectPosition = input<string>('object-center');
    role = input<'img' | 'presentation' | undefined>(undefined);
    loaded = output<void>();

    content = contentChildren(StoryImageSourceComponent);
    environment = inject(EnvironmentService);

    cdn = this.environment.getApiUrl('imagor');
    baseUrl = this.environment.getEnvironment().application.baseUrl?.replace('https://', '');

    computedSources = computed(() => {
        const contents = this.content() ?? [];
        const sources: StorySource[] = [];
        for (const source of contents) {
            const media = source.media() ?? '';
            const src = source.src?.() ?? '';

            if (source.allowAvif()) {
                sources.push({
                    type: 'image/avif',
                    src: `${this.getCDNPath(src, 'avif', source.width() ?? 0, source.height() ?? 0)} 1x,
                    ${this.getCDNPath(src, 'avif', (source.width() ?? 0) * 2, (source.height() ?? 0) * 2)} 2x,
                    `,
                    media,
                });
            }

            if (source.allowWebP()) {
                sources.push({
                    type: 'image/webp',
                    src: `${this.getCDNPath(src, 'webp', source.width() ?? 0, source.height() ?? 0)} 1x,
                    ${this.getCDNPath(src, 'webp', (source.width() ?? 0) * 2, (source.height() ?? 0) * 2)} 2x,
                    `,
                    media,
                });
            }

            const type = source.src()?.split('.').pop() ?? '';

            sources.push({
                type: ('image/' + type) as
                    | 'image/jpeg'
                    | 'image/png'
                    | 'image/gif'
                    | 'image/svg+xml'
                    | 'image/tiff'
                    | 'image/bmp'
                    | 'image/ico',
                src: this.getCDNPath(
                    src,
                    type as 'webp' | 'avif' | 'jpeg' | 'png' | 'gif' | 'svg' | 'tiff' | 'bmp' | 'ico',
                    source.width() ?? 0,
                    source.height() ?? 0
                ),
                media,
            });
        }
        return sources;
    });

    computedSrc = computed(() => {
        const contents = this.content() ?? [];

        if (contents.length) {
            // TODO sanitize src to remove query params and hash

            return this.sanitizer.sanitize(SecurityContext.URL, contents[contents.length - 1].src?.() ?? '') ?? '';
        }
        return '';
    });

    getCDNPath(
        src: string,
        format: 'webp' | 'avif' | 'jpeg' | 'png' | 'gif' | 'svg' | 'tiff' | 'bmp' | 'ico',
        width: number,
        height: number,
        quality = 90
    ) {
        if (!src) {
            return '';
        }
        return (
            this.sanitizer.sanitize(
                SecurityContext.URL,
                `${this.cdn}/unsafe/${width ?? 'auto'}x${height ?? 'auto'}/smart/filters:format(${format}):quality(${quality})/${this.baseUrl}/${src}`
            ) ?? ''
        );
    }
}
